import { useState } from 'react';
import { FeedbackType } from '@property/shared';
import { useSubmitFeedback } from '@/hooks/use-feedback-mutations';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, Star } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface ReportFeedbackDialogProps {
  inspectionId: string;
}

export function ReportFeedbackDialog({ inspectionId }: ReportFeedbackDialogProps) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const submitFeedback = useSubmitFeedback();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-10 px-4">
          <MessageSquare className="mr-2 h-4 w-4" />
          Feedback
        </Button>
      </DialogTrigger>
      <DialogContent className="bottom-0 left-0 top-auto max-w-full translate-x-0 translate-y-0 rounded-t-2xl rounded-b-none border-border bg-background data-[state=open]:slide-in-from-bottom-8 data-[state=closed]:slide-out-to-bottom-8 sm:bottom-auto sm:left-[50%] sm:top-[50%] sm:max-w-[425px] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl">
        <DialogHeader>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-sidebar-primary">
            Field notes
          </p>
          <DialogTitle className="font-display text-xl font-medium tracking-tight text-foreground">
            Report Feedback
          </DialogTitle>
          <DialogDescription className="text-sm text-foreground/60">
            Your feedback helps us improve our field verification accuracy.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="space-y-3">
            <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/45">
              Rating
            </Label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="transition-transform hover:scale-110 active:scale-95"
                >
                  <Star
                    className={`h-8 w-8 ${rating >= star ? 'fill-amber-400 text-amber-400' : 'text-border-strong'}`}
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <Label
              htmlFor="feedback-comment"
              className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/45"
            >
              Comments
            </Label>
            <Textarea
              id="feedback-comment"
              placeholder="What can we improve in this report?"
              className="min-h-[120px] rounded-sm border-border bg-card text-sm text-foreground/80"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter className="flex-col gap-2">
          {submitFeedback.isError && (
            <p className="text-sm text-destructive text-center">
              Failed to submit feedback. Please try again.
            </p>
          )}
          <Button
            className="h-11 w-full"
            disabled={submitFeedback.isPending || feedback.trim().length === 0}
            onClick={async () => {
              await submitFeedback.mutateAsync({
                type: FeedbackType.INSPECTION_REPORT,
                referenceId: inspectionId,
                rating: rating > 0 ? rating : undefined,
                message: feedback.trim(),
              });
              setOpen(false);
              setRating(0);
              setFeedback('');
              submitFeedback.reset();
            }}
          >
            {submitFeedback.isPending ? 'Submitting…' : 'Submit Feedback'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
